import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t, plural } from '@lingui/core/macro';
import { client } from '../services/apiClient';
import type {
  BookmarkAction,
  BookmarkCollectionResponse,
  BookmarkSectionActionTemplate,
  BookmarkSectionBatchTarget,
} from '../types/api';
import { useToast } from './useToast';
import { removeBookmarksFromSearchCaches } from '../services/bookmarkActions';

const SECTION_ASYNC_TOAST_MS = 10000;

type BookmarkListSnapshot = [
  readonly unknown[],
  BookmarkCollectionResponse | undefined,
][];

export function useBatchActions() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  // Invalidate the list caches, sidebar counts, and each affected bookmark's
  // individual detail cache. The per-bookmark `['bookmark', id]` invalidation
  // matches what the single-item mutations do (see invalidateBookmarkQueries in
  // bookmarkActions.ts) so an open reader column doesn't keep showing stale
  // state (e.g. archive/like status) after a drag/drop batch action.
  const invalidateAfterBatch = (bookmarkIds: string[]) => {
    queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    bookmarkIds.forEach((id) => {
      queryClient.invalidateQueries({ queryKey: ['bookmark', String(id)] });
    });
  };

  // Optimistically drop the given bookmarks from every cached bookmark list so
  // the rows disappear immediately for actions that remove them from the current
  // view (archive/delete/move/unarchive/move-home). Previously these relied only
  // on a row exit animation plus the onSuccess refetch — but the animation clears
  // itself after ~250ms, so if the refetch hadn't landed the row snapped back into
  // view and the item looked as though it had never been archived. Removing it
  // from the cache up front (and rolling back on error) mirrors
  // optimisticallyRemoveFromLists in bookmarkActions.ts and makes drag/drop batch
  // actions behave like the single-item ones. Returns a snapshot for onError.
  const removeBookmarksFromListCaches = async (
    bookmarkIds: string[],
  ): Promise<BookmarkListSnapshot> => {
    const idSet = new Set(bookmarkIds.map(String));
    await queryClient.cancelQueries({ queryKey: ['bookmarks'] });
    const previousLists = queryClient.getQueriesData<BookmarkCollectionResponse>({
      predicate: (query) =>
        Array.isArray(query.queryKey) && query.queryKey[0] === 'bookmarks',
    });
    previousLists.forEach(([queryKey]) => {
      queryClient.setQueryData(
        queryKey,
        (old: BookmarkCollectionResponse | undefined) => {
          if (!old?.bookmarks) return old;
          return {
            ...old,
            bookmarks: old.bookmarks.filter((b) => !idSet.has(String(b.id))),
          };
        },
      );
    });
    return previousLists;
  };

  const restoreListCaches = (previousLists: BookmarkListSnapshot | undefined) => {
    previousLists?.forEach(([queryKey, data]) => {
      queryClient.setQueryData(queryKey, data);
    });
  };

  const batchArchive = useMutation({
    mutationFn: async (bookmarkIds: string[]) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'archive',
      }));
      await client.bookmarkActions(actions);
      return bookmarkIds;
    },
    onMutate: async (bookmarkIds) => {
      const previousLists = await removeBookmarksFromListCaches(bookmarkIds);
      return { previousLists };
    },
    onSuccess: (bookmarkIds) => {
      invalidateAfterBatch(bookmarkIds);
      showToast(
        plural(bookmarkIds.length, {
          one: 'Archived # article',
          other: 'Archived # articles',
        }),
        'success',
      );
    },
    onError: (_err, _bookmarkIds, context) => {
      restoreListCaches(context?.previousLists);
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchDelete = useMutation({
    mutationFn: async (bookmarkIds: string[]) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'delete',
      }));
      await client.bookmarkActions(actions);
      return bookmarkIds;
    },
    onMutate: async (bookmarkIds) => {
      const previousLists = await removeBookmarksFromListCaches(bookmarkIds);
      return { previousLists };
    },
    onSuccess: (bookmarkIds) => {
      invalidateAfterBatch(bookmarkIds);
      // Delete is the one action that also drops out of search results.
      removeBookmarksFromSearchCaches(queryClient, bookmarkIds);
      showToast(
        plural(bookmarkIds.length, {
          one: 'Deleted # article',
          other: 'Deleted # articles',
        }),
        'success',
      );
    },
    onError: (_err, _bookmarkIds, context) => {
      restoreListCaches(context?.previousLists);
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchMove = useMutation({
    mutationFn: async ({
      bookmarkIds,
      folderId,
    }: {
      bookmarkIds: string[];
      folderId: string;
    }) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'move',
        folder_id: folderId,
      }));
      await client.bookmarkActions(actions);
      return { bookmarkIds, folderId };
    },
    onMutate: async ({ bookmarkIds }) => {
      const previousLists = await removeBookmarksFromListCaches(bookmarkIds);
      return { previousLists };
    },
    onSuccess: ({ bookmarkIds }) => {
      invalidateAfterBatch(bookmarkIds);
      showToast(
        plural(bookmarkIds.length, {
          one: 'Moved # article',
          other: 'Moved # articles',
        }),
        'success',
      );
    },
    onError: (_err, _vars, context) => {
      restoreListCaches(context?.previousLists);
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchTag = useMutation({
    mutationFn: async ({
      bookmarkIds,
      addTags = [],
      removeTags = [],
    }: {
      bookmarkIds: string[];
      addTags?: string[];
      removeTags?: string[];
    }) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'tags',
        add_tags: addTags.map((tag) => ({ name: tag })),
        remove_tags: removeTags.map((tag) => ({ name: tag })),
      }));
      await client.bookmarkActions(actions);
      return { bookmarkIds, addTags, removeTags };
    },
    onSuccess: ({ bookmarkIds }) => {
      invalidateAfterBatch(bookmarkIds); // also refreshes sidebar tag counts via currentUser
      showToast(
        plural(bookmarkIds.length, {
          one: 'Updated tags for # article',
          other: 'Updated tags for # articles',
        }),
        'success',
      );
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchLike = useMutation({
    mutationFn: async (bookmarkIds: string[]) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'like',
      }));
      await client.bookmarkActions(actions);
      return bookmarkIds;
    },
    onSuccess: (bookmarkIds) => {
      invalidateAfterBatch(bookmarkIds);
      showToast(
        plural(bookmarkIds.length, {
          one: 'Liked # article',
          other: 'Liked # articles',
        }),
        'success',
      );
    },
    onError: () => {
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchUnarchive = useMutation({
    mutationFn: async (bookmarkIds: string[]) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'home',
      }));
      await client.bookmarkActions(actions);
      return bookmarkIds;
    },
    onMutate: async (bookmarkIds) => {
      const previousLists = await removeBookmarksFromListCaches(bookmarkIds);
      return { previousLists };
    },
    onSuccess: (bookmarkIds) => {
      invalidateAfterBatch(bookmarkIds);
      showToast(
        plural(bookmarkIds.length, {
          one: 'Unarchived # article',
          other: 'Unarchived # articles',
        }),
        'success',
      );
    },
    onError: (_err, _bookmarkIds, context) => {
      restoreListCaches(context?.previousLists);
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const batchMoveHome = useMutation({
    mutationFn: async (bookmarkIds: string[]) => {
      const actions: BookmarkAction[] = bookmarkIds.map((id) => ({
        bookmark_id: id,
        action: 'home',
      }));
      await client.bookmarkActions(actions);
      return bookmarkIds;
    },
    onMutate: async (bookmarkIds) => {
      const previousLists = await removeBookmarksFromListCaches(bookmarkIds);
      return { previousLists };
    },
    onSuccess: (bookmarkIds) => {
      invalidateAfterBatch(bookmarkIds);
      showToast(
        plural(bookmarkIds.length, {
          one: 'Moved # article',
          other: 'Moved # articles',
        }),
        'success',
      );
    },
    onError: (_err, _bookmarkIds, context) => {
      restoreListCaches(context?.previousLists);
      showToast(t`Sorry, there was an error. Please try again.`, 'error');
    },
  });

  const afterSectionMutationSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['bookmarks'] });
    queryClient.invalidateQueries({ queryKey: ['currentUser'] });
  };

  const batchSectionArchive = useMutation({
    mutationFn: async (section: BookmarkSectionBatchTarget) => {
      const action: BookmarkSectionActionTemplate = { action_type: 'archive' };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Archiving articles… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Archived articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to archive articles`, 'error');
    },
  });

  const batchSectionDelete = useMutation({
    mutationFn: async (section: BookmarkSectionBatchTarget) => {
      const action: BookmarkSectionActionTemplate = { action_type: 'delete' };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Deleting articles… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Deleted articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to delete articles`, 'error');
    },
  });

  const batchSectionMove = useMutation({
    mutationFn: async ({
      section,
      folderId,
    }: {
      section: BookmarkSectionBatchTarget;
      folderId: string;
    }) => {
      const action: BookmarkSectionActionTemplate = {
        action_type: 'move',
        folder_id: folderId,
      };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Moving articles… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Moved articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to move articles`, 'error');
    },
  });

  // Same backend action as batchSectionUnarchive ('home'), but with "move"
  // toast wording for the folder-view move picker's Home option.
  const batchSectionMoveHome = useMutation({
    mutationFn: async (section: BookmarkSectionBatchTarget) => {
      const action: BookmarkSectionActionTemplate = { action_type: 'home' };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Moving articles… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Moved articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to move articles`, 'error');
    },
  });

  const batchSectionTag = useMutation({
    mutationFn: async ({
      section,
      addTags = [],
      removeTags = [],
    }: {
      section: BookmarkSectionBatchTarget;
      addTags?: string[];
      removeTags?: string[];
    }) => {
      const action: BookmarkSectionActionTemplate = {
        action_type: 'tags',
        add_tags: addTags.map((tag) => ({ name: tag })),
        remove_tags: removeTags.map((tag) => ({ name: tag })),
      };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Updating tags… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Updated tags for articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to update tags`, 'error');
    },
  });

  const batchSectionUnarchive = useMutation({
    mutationFn: async (section: BookmarkSectionBatchTarget) => {
      const action: BookmarkSectionActionTemplate = { action_type: 'home' };
      return client.bookmarkSectionAction(section, action);
    },
    onSuccess: (res) => {
      afterSectionMutationSuccess();
      if (res.async) {
        showToast(
          t`Restoring articles… this may take a few minutes to complete.`,
          'success',
          SECTION_ASYNC_TOAST_MS,
        );
      } else {
        showToast(t`Unarchived articles`, 'success');
      }
    },
    onError: () => {
      showToast(t`Failed to unarchive articles`, 'error');
    },
  });

  return {
    batchArchive,
    batchDelete,
    batchMove,
    batchMoveHome,
    batchTag,
    batchLike,
    batchUnarchive,
    batchSectionArchive,
    batchSectionDelete,
    batchSectionMove,
    batchSectionMoveHome,
    batchSectionTag,
    batchSectionUnarchive,
  };
}
